import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { sendIntakeFormToClient } from "utils/clientIntakeFormService";

const SendIntakeFormDialog = ({ open, onClose, clientId, clientName, clientEmail }) => {
  const [note, setNote] = useState("");

  const { mutate: sendForm, isPending } = useMutation({
    mutationFn: () => sendIntakeFormToClient(clientId, note),
    onSuccess: () => {
      toast.success(`Intake form sent to ${clientEmail || clientName || "client"}`);
      setNote("");
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage || "Failed to send intake form");
    },
  });

  const handleClose = () => {
    if (!isPending) {
      setNote("");
      onClose();
    }
  };

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="font-semibold text-app-text text-base">Send Intake Form</DialogTitle>
      <DialogContent className="pt-2 space-y-4">
        <p className="text-sm text-app-text-secondary">
          An intake form will be sent to{" "}
          <strong>{clientEmail || clientName || "the client"}</strong> via email. The client can
          fill in their details through a unique secure link.
        </p>
        <TextField
          label="Optional note to include in the email"
          multiline
          rows={3}
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Please complete this form at your earliest convenience."
          size="small"
          disabled={isPending}
        />
        {!clientEmail && (
          <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
            Warning: No email address on file for this client. The form cannot be sent.
          </p>
        )}
      </DialogContent>
      <DialogActions className="gap-2 px-6 pb-5">
        <button
          type="button"
          onClick={handleClose}
          disabled={isPending}
          className="px-4 py-2 text-app-text-secondary hover:bg-app-surface-muted rounded-lg text-sm font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => sendForm()}
          disabled={isPending || !clientEmail}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          {isPending && <CircularProgress size={16} style={{ color: "white" }} />}
          Send Form
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default SendIntakeFormDialog;
